import {Hotkey} from '@wandb/components';
import {clamp} from 'lodash';
import React from 'react';

import {Slider} from '../controls/Slider';
import {useMediaBrowserModalContext} from './MediaBrowserModalContext';

export const MediaBrowserModalFooter = React.memo(() => {
  const {updateStep, updateIndex, sliderData, sliderConfig} =
    useMediaBrowserModalContext();

  const updateStepWithConstraints = (value: number) => {
    updateStep(clamp(value, stepSlider?.min ?? 0, stepSlider?.max ?? 0));
  };

  const updateIndexWithConstraints = (value: number) => {
    updateIndex(clamp(value, indexSlider?.min ?? 0, indexSlider?.max ?? 0));
  };

  const {stepSlider, indexSlider} = sliderData;

  // Configuration objects to reduce duplication
  const sliderConfigs = {
    step: {
      label: stepSlider?.label ?? 'Step',
      tooltipContent: (
        <>
          <Hotkey keys={['Primary', 'ArrowRight']} name="Next step" m={2} />
          or
          <Hotkey keys={['Primary', 'ArrowLeft']} name="Previous step" m={2} />
        </>
      ),
      dataTestId: 'step-slider-root',
      strideLength: sliderConfig?.stepStrideLength,
      snapToExistingStep: sliderConfig?.snapToExistingStep,
    },
    index: {
      label: 'Index',
      tooltipContent: (
        <>
          <Hotkey keys={['Alt', 'ArrowRight']} name="Next index" m={2} />
          or
          <Hotkey keys={['Alt', 'ArrowLeft']} name="Previous index" m={2} />
        </>
      ),
      dataTestId: 'index-slider-root',
      strideLength: 1,
    },
  };

  if (!stepSlider && !indexSlider) {
    return null;
  }

  const stepSliderActive =
    stepSlider && stepSlider.max > 0 && stepSlider.min < stepSlider.max;
  const indexSliderActive = indexSlider && indexSlider.max > 0;

  return (
    <div className="w-full bg-wb-surface px-16 py-16 border-t border-wb-border">
      <div className="flex w-full items-center justify-center">
        <div className="flex w-full items-center justify-center gap-40">
          {stepSliderActive && stepSlider && (
            <Slider
              label={sliderConfigs.step.label}
              min={stepSlider.min}
              max={stepSlider.max}
              current={stepSlider.current}
              onChange={updateStepWithConstraints}
              tooltipContent={sliderConfigs.step.tooltipContent}
              data-test={sliderConfigs.step.dataTestId}
              strideLength={sliderConfigs.step.strideLength}
              values={stepSlider.steps}
              showTicks={false}
              snapToExistingValue={sliderConfigs?.step?.snapToExistingStep}
              maxWidth="400px"
              showLabel={true}
              showInput={true}
            />
          )}

          {indexSliderActive && indexSlider && (
            <Slider
              label={sliderConfigs.index.label}
              min={indexSlider.min}
              max={indexSlider.max}
              current={indexSlider.current}
              onChange={updateIndexWithConstraints}
              tooltipContent={sliderConfigs.index.tooltipContent}
              data-test={sliderConfigs.index.dataTestId}
              strideLength={sliderConfigs.index.strideLength}
              values={indexSlider.indices}
              showTicks={false}
              snapToExistingValue={true}
              maxWidth="400px"
              showLabel={true}
              showInput={true}
            />
          )}
        </div>
      </div>
    </div>
  );
});
